import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import StatusIndicator from "../../../components/ui/StatusIndicator";
import { useSelector } from "react-redux";
import { User } from "lucide-react";
import { selectInvoiceStatusById } from "../invoiceProgressSlice";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { useCancelUpload } from "../useCancelUpload";
const InvoiceCard = ({
  invoice,
  viewMode = "timeline",
  onRetry,
  onViewDetails,
  onCorrectError,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const cancelUpload = useCancelUpload();

  // Invoice name could be in invoice.invoiceName or invoice.name
  const invoiceName = invoice?.filename || invoice?.name;

  // Get real-time invoice data from Redux store
  const invoiceData = useSelector((state) =>
    selectInvoiceStatusById(state, invoice?.batchId, invoiceName),
  );

  // // Debug logging
  // console.log("Invoice Name:", invoiceName);
  // console.log("Invoice Object:", invoice);
  // console.log("InvoiceData from Redux:", invoiceData);
  // console.log("Progress array:", invoiceData?.progress);

  // Get current status (last item in progress array)
  const currentStatus =
    invoiceData?.progress?.length > 0
      ? invoiceData.progress[invoiceData.progress.length - 1]
      : null;

  console.log("Current Status:", currentStatus);

  useEffect(() => {
    if (currentStatus === "error") {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 600);
      return () => clearTimeout(timer);
    }
  }, [currentStatus]);

  const getStatusConfig = (status) => {
    switch (status) {
      case "uploaded":
        return {
          color: "text-accent",
          bgColor: "bg-accent/10",
          borderColor: "border-accent/20",
        };
      case "processed":
        return {
          color: "text-accent",
          bgColor: "bg-accent/10",
          borderColor: "border-accent/20",
        };
      case "validating":
        return {
          color: "text-success",
          bgColor: "bg-success/10",
          borderColor: "border-success/20",
        };
      case "subitted":
        return {
          color: "text-success",
          bgColor: "bg-success/10",
          borderColor: "border-success/20",
        };
      case "error":
        return {
          color: "text-error",
          bgColor: "bg-error/10",
          borderColor: "border-error/20",
        };
      default:
        return {
          color: "text-muted-foreground",
          bgColor: "bg-muted",
          borderColor: "border-border",
        };
    }
  };

  const statusConfig = getStatusConfig(currentStatus);

  // Calculate progress based on 4 steps: uploaded, processed, validating, submitted
  const getProgressPercentage = () => {
    if (!currentStatus) return 0;
    const steps = ["uploaded", "processed", "validating", "submitted"];
    const currentStepIndex = steps.indexOf(currentStatus);
    if (currentStepIndex === -1) return 0;
    return ((currentStepIndex + 1) / steps.length) * 100;
  };

  const progressPercentage = getProgressPercentage();

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleRetry = () => {
    onRetry?.(invoice?.id);
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 300);
  };

  return (
    <div
      className={`
      bg-muted border rounded-lg shadow-sm transition-all duration-300 ease-out
      ${statusConfig?.borderColor} ${isAnimating ? "animate-pulse" : ""}
      ${currentStatus === "error" && isAnimating ? "animate-bounce" : ""}
      hover:shadow-md hover:scale-[1.01]
    `}
    >
      {/* Card Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div
              className={`
              flex items-center justify-center w-10 h-10 rounded-lg
              ${statusConfig?.bgColor} ${statusConfig?.color}
            `}
            >
              <Icon
                name="FileText"
                size={18}
                color="currentColor"
                strokeWidth={2}
              />
            </div>
            <div>
              <h3 className="font-semibold text-foreground truncate max-w-[200px]">
                {invoice?.filename}
              </h3>
              {invoice?.batchName && (
                <p className="text-xs text-gray-500 mt-0.5">
                  Batch: {invoice?.batchName}
                </p>
              )}
              {/* <p className="text-sm text-muted-foreground">
                Uploaded: {formatTimestamp(invoice?.uploadTime)}
              </p> */}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <StatusIndicator
              status={currentStatus}
              label={
                currentStatus?.charAt(0)?.toUpperCase() +
                currentStatus?.slice(1)
              }
              size="sm"
              showPulse={
                currentStatus === "processing" ||
                currentStatus === "uploaded" ||
                currentStatus === "validating"
              }
            />

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsExpanded(!isExpanded)}
              className="h-8 w-8"
            >
              <Icon
                name={isExpanded ? "ChevronUp" : "ChevronDown"}
                size={16}
                color="currentColor"
              />
            </Button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              Step {invoiceData?.progress?.length || 0} of 4
            </span>
            <span className="text-foreground font-medium">
              {Math.round(progressPercentage)}%
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
            <div
              className={`
                h-full transition-all duration-500 ease-out
                ${
                  currentStatus === "error"
                    ? "bg-error"
                    : "bg-gradient-to-r from-primary to-accent"
                }
              `}
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      </div>
      {/* Expandable Content */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Processing Steps */}
          <div className="space-y-3">
            <h4 className="font-medium text-foreground">Processing Steps</h4>
            {invoiceData?.progress?.map((step, index) => {
              // Check if this step is completed (not the last step)
              const isCompleted = index < invoiceData.progress.length - 1;
              const isCurrentStep = index === invoiceData.progress.length - 1;

              return (
                <div key={index} className="flex items-center space-x-3">
                  <div
                    className={`
                  flex items-center justify-center w-6 h-6 rounded-full
                  ${
                    step === "error"
                      ? "bg-error text-white"
                      : isCompleted || step === "submitted"
                        ? "bg-success text-white"
                        : isCurrentStep && step === "validating"
                          ? "bg-accent text-white"
                          : "bg-muted text-muted-foreground"
                  }
                `}
                  >
                    {(isCompleted || step === "submitted") &&
                      step !== "error" && (
                        <Icon
                          name="Check"
                          size={12}
                          color="currentColor"
                          strokeWidth={2}
                        />
                      )}
                    {isCurrentStep && step === "validating" && (
                      <Icon
                        name="Loader2"
                        size={12}
                        color="currentColor"
                        strokeWidth={2}
                        className="animate-spin"
                      />
                    )}
                    {step === "error" && (
                      <Icon
                        name="X"
                        size={12}
                        color="currentColor"
                        strokeWidth={2}
                      />
                    )}
                    {step === "pending" && (
                      <span className="text-xs font-bold">{index + 1}</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span
                        className={`
                      text-sm font-medium
                      ${step === "error" ? "text-error" : "text-foreground"}
                    `}
                      >
                        {step}
                      </span>
                      {/* {step && (
                      <span className="text-xs text-muted-foreground">
                        {step?.duration}ms
                      </span>
                    )} */}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Error Correction Interface */}
          {invoice?.status === "error" && (
            <div className="bg-error/5 border border-error/20 rounded-lg p-4 space-y-3">
              <div className="flex items-center space-x-2">
                <Icon
                  name="AlertTriangle"
                  size={16}
                  color="var(--color-error)"
                  strokeWidth={2}
                />
                <h5 className="font-medium text-error">
                  Error Correction Required
                </h5>
              </div>
              <p className="text-sm text-muted-foreground">
                {invoice?.errorMessage ||
                  "Validation failed. Please review and correct the issues below."}
              </p>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    onCorrectError?.(
                      invoiceName || invoice?.filename || invoice?.name,
                      invoice?.batchId,
                    )
                  }
                  iconName="Edit"
                  iconPosition="left"
                >
                  Correct Error
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRetry}
                  iconName="RotateCcw"
                  iconPosition="left"
                >
                  Retry
                </Button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="text-xs text-muted-foreground">
              Invoice ID: {invoice?.recordId}
            </div>
            <div className="flex items-center space-x-2">
              {currentStatus !== "submitted" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    console.log("Calling onViewDetails with:", invoiceName);
                    console.log("Invoice object:", invoice);
                    onViewDetails(
                      invoiceName || invoice?.filename || invoice?.name,
                      invoice.batchId,
                    );
                  }}
                  iconName="Eye"
                  iconPosition="left"
                >
                  View Details
                </Button>
              )}
              {currentStatus !== "submitted" &&
                invoice?.status !== "completed" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowCancelConfirm(true)}
                    iconName="Trash2"
                    iconPosition="left"
                    className="text-error hover:text-error"
                  >
                    Cancel
                  </Button>
                )}
              {invoice?.status === "completed" && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() =>
                    (window.location.href = "/processing-completion-summary")
                  }
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  View Summary
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showCancelConfirm}
        title="Cancel this invoice?"
        message={`This permanently deletes "${invoice?.filename}" and its uploaded file from storage. This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        isLoading={cancelUpload.isPending || cancelUpload.isLoading}
        onConfirm={() =>
          cancelUpload.mutate(invoice?.recordId, {
            onSuccess: () => setShowCancelConfirm(false),
            onError: () => setShowCancelConfirm(false),
          })
        }
        onClose={() => setShowCancelConfirm(false)}
      />
    </div>
  );
};

export default InvoiceCard;
